import { useCallback } from "react";
import { useExternalProviderDashboard } from "./hooks/use-external-provider-dashboard";
import { useEvaluationSubmit } from "./hooks/use-evaluation-submit";
import { useInvoiceSubmit } from "./hooks/use-invoice-submit";
import { useRequestDataSubmit } from "./hooks/use-request-data-submit";
import { useContractInitialization } from "./hooks/use-contract-initialization";
import { useExternalProviderRepository } from "./context/external-provider-repository.context";
import { useAppDispatch } from "@redux/hooks";
import { initializeContract } from "@academy-context/write/application/use-cases/external-provider/subcontracting-contract-enable/enable-subcontracting-contract";
import { ExternalProviderPortalSkeleton } from "./external-provider-portal.skeleton";
import { DashboardHeader } from "./components/dashboard-header.content";
import { ProgressStepper } from "./components/progress-stepper.content";
import { RequestDataSection } from "./components/request-data-section/request-data-section.content";
import { RequestDataReadOnly } from "./components/request-data-section/request-data-read-only.content";
import { InvoiceEditablePhase } from "./components/phases/invoice-editable-phase.content";
import { InvoiceReadOnlyPhase } from "./components/phases/invoice-readonly-phase.content";
import { EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS } from "@academy-context/shared/domain/types/enums/external-provider-batch-request";
import type { SubmitEvaluationBody } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import { isEvaluationSubmitted } from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { ExternalProviderRepositoryProvider } from "./context/external-provider-repository.context";
import { SignedContractCard } from "./components/signed-contract-card.content";
import { UnsignedContractCard, UnsignableContractCard } from "./components/unsigned-contract-card.content";
import { ContractSignatureDialog } from "./components/contract-signature-dialog.content";
import { SUBCONTRACTING_CONTRACT_STATUS } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import { getPhaseFromStatus } from "./utils/get-phase-from-status";
import { DashboardPageLayout } from "@shared-kernel/primary/shared/layout/dashboard-page-layout";
import type { ExternalProviderDashboardVM } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type { ContractSignatory } from "@academy-context/shared/domain/types/contract-signatory";

interface Props {
  accessToken: string;
}

const ExternalProviderPortalContent = () => {
  const dispatch = useAppDispatch();
  const repository = useExternalProviderRepository();
  const { data, fetching, error, refetch } = useExternalProviderDashboard();
  const { submit } = useEvaluationSubmit(refetch);
  const invoiceSubmit = useInvoiceSubmit();
  const {
    processing: contractInitializeProcessing,
    isDialogOpen,
    signatureLink,
    contractSignedOptimistically,
    handleSignatureDone,
    handleCloseDialog,
    handleDeclined,
    handleError,
  } = useContractInitialization();

  const handleRequestDataSuccess = useCallback(() => {
    refetch();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [refetch]);

  const handleInitializeContract = useCallback(
    (signatory: ContractSignatory) => {
      dispatch(initializeContract({ externalProviderRepository: repository, body: { signatory } }));
    },
    [dispatch, repository],
  );

  const requestDataSubmit = useRequestDataSubmit(handleRequestDataSuccess);

  // Wrapper to convert Promise<boolean> to Promise<void> for type compatibility
  const handleEvaluationSubmit = useCallback(
    async (participationId: string, body: SubmitEvaluationBody): Promise<void> => {
      await submit(participationId, body);
    },
    [submit],
  );

  const renderContent = (data: ExternalProviderDashboardVM) => {
    // Determine current status (use optimistic update for invoice submission)
    const currentStatus = invoiceSubmit.invoiceSubmittedSuccessfully
      ? EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.INVOICE_TO_VALIDATE_BY_ADMIN
      : data.status;
    const phase = getPhaseFromStatus(currentStatus);

    const evaluationSubmitted = isEvaluationSubmitted(data);

    const isAfterRequestApproval = phase !== "review" && phase !== "pending_validation";
    const isContractPendingSignature =
      !data.request.subcontractingContract || data.request.subcontractingContract.status === SUBCONTRACTING_CONTRACT_STATUS.PENDING;
    const isContractSigned =
      contractSignedOptimistically || data.request.subcontractingContract?.status === SUBCONTRACTING_CONTRACT_STATUS.SIGNED;

    const showUnsignedContractCard = isAfterRequestApproval && !contractSignedOptimistically && isContractPendingSignature;
    const showSignedContractCard = isAfterRequestApproval && isContractSigned;
    // Null on requests issued before the legal representative was required: the contract cannot be signed
    const legalRepresentative = data.provider.legalRepresentative;

    const renderPhase = () => {
      switch (phase) {
        case "review":
          return <RequestDataSection data={data} requestDataSubmit={requestDataSubmit} />;

        case "pending_validation":
          return <RequestDataReadOnly data={data} />;

        case "training_in_progress":
          return <RequestDataReadOnly data={data} />;

        case "invoice_editable":
          return (
            <InvoiceEditablePhase
              data={data}
              currentStatus={currentStatus}
              invoiceSubmit={invoiceSubmit}
              onEvaluationSubmit={handleEvaluationSubmit}
            />
          );

        case "invoice_readonly":
          return (
            <InvoiceReadOnlyPhase
              data={data}
              onEvaluationSubmit={handleEvaluationSubmit}
              optimisticInvoice={invoiceSubmit.submittedInvoiceData}
            />
          );
      }
    };

    return (
      <>
        <ProgressStepper currentStatus={currentStatus} trainingEndDate={data.training.interval.end} />
        <DashboardHeader
          currentStatus={currentStatus}
          isEvaluationSubmitted={evaluationSubmitted}
          isInvoiceSubmitted={data.invoice !== null}
        />
        {showUnsignedContractCard &&
          (legalRepresentative ? (
            <UnsignedContractCard
              contract={data.request.subcontractingContract}
              legalRepresentative={legalRepresentative}
              providerEmail={data.provider.email}
              isInitializing={contractInitializeProcessing === "pending"}
              onInitializeContract={handleInitializeContract}
            />
          ) : (
            <UnsignableContractCard />
          ))}
        {renderPhase()}
        {showSignedContractCard && <SignedContractCard contract={data.request.subcontractingContract} />}
        <ContractSignatureDialog
          isOpen={isDialogOpen}
          signatureLink={signatureLink}
          onSignatureDone={handleSignatureDone}
          onClose={handleCloseDialog}
          onDeclined={handleDeclined}
          onError={handleError}
        />
      </>
    );
  };

  return (
    <DashboardPageLayout
      fetching={fetching}
      data={data}
      loadingComponent={<ExternalProviderPortalSkeleton />}
      errorStateProps={{
        title: "Erreur",
        description: error || "Une erreur est survenue lors du chargement des données.",
      }}
      emptyStateProps={{
        title: "Aucune donnée disponible",
      }}
      className="mt-8 flex flex-col justify-center space-y-6"
    >
      {renderContent}
    </DashboardPageLayout>
  );
};

export const ExternalProviderPortalContainer = ({ accessToken }: Props) => {
  return (
    <ExternalProviderRepositoryProvider accessToken={accessToken}>
      <ExternalProviderPortalContent />
    </ExternalProviderRepositoryProvider>
  );
};
